import { useCallback, useState } from 'react'
import { Formik, Field, Form, FormikErrors } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import { toast } from 'react-toastify'

import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import CheckAccordion from 'components/CheckAccordion'
import Loader from 'components/Loader'

import { PERMISSIONS_APPS } from 'resources/PERMISSIONS_APPS'
import { useGetRolesQuery, useUpdateRoleMutation } from 'store/porticoApi/roles'
import {
  useCreatePermissionMutation,
  useDeletePermissionMutation,
  useUpdatePermissionMutation,
} from 'store/porticoApi/permissions'
import { TAppTypesDisplaySubsitutes, TAppTypesApiSubstitutes } from 'types/apps'
import { TActions, TActionsApiSubstitutes } from 'types/actions'
import { TPermission } from 'types/permissions'
import { convertActionToApiValue } from 'utils/rolesPermissions/convertAction'
import { convertDisplayAppNameToApi } from 'utils/rolesPermissions/convertAppNames'
import { setDefaultSelectOption } from 'utils/rolesPermissions/setDefaultSelectOption'
import { setCheckedPermission } from 'utils/rolesPermissions/setCheckedPermission'
import { setEditRoleValues } from 'utils/rolesPermissions/setEditRoleValues'
import { transformPermissionsToFrontendSubstitutesNames } from 'utils/rolesPermissions/transformPermissionsToFrontendSubstitutesNames'
import { PERMISSIONS_API_APPS_NAMES } from 'resources/PERMISSIONS_APPS_NAMES'

type TReturnDropdownValue = {
  name: TAppTypesDisplaySubsitutes
  option: string
  isActiveCheckAccordion: boolean
}

type TFormValues = {
  name: string
  permissions: TPermission[]
  deletedPermissions: TPermission[]
}

const EditRole = () => {
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const navigate = useNavigate()
  const { id } = useParams()

  const { data: role, isFetching } = useGetRolesQuery(undefined, {
    selectFromResult: ({ data, isFetching }) => ({
      data: data?.filter((role) => role.uuid === id)[0],
      isFetching,
    }),
  })
  const [updateRole] = useUpdateRoleMutation()
  const [createPermission] = useCreatePermissionMutation()
  const [updatePermission] = useUpdatePermissionMutation()
  const [deletePermission] = useDeletePermissionMutation()

  // filtered permissions by subject, we have to filter permissions which are not included in PERMISSIONS_API_APPS_NAMES
  // because in this form we display only permission with subjects from PERMISSIONS_API_APPS_NAMES
  const permissionsIncludesApiAppNames = role?.permissions?.filter(({ subject }) =>
    PERMISSIONS_API_APPS_NAMES.includes(subject)
  )

  const initialValues: TFormValues = {
    name: role?.name ?? '',
    permissions:
      transformPermissionsToFrontendSubstitutesNames(
        permissionsIncludesApiAppNames as TPermission[]
      ) ?? [],
    deletedPermissions: [],
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
  })

  const disableUpdateButton = useCallback(
    (dirty: boolean, errors: FormikErrors<TFormValues>) => {
      const disableButton = !dirty || (showValidationErrors && !isEmpty(errors))

      return disableButton
    },
    [showValidationErrors]
  )

  const onClickSaveButton = useCallback(() => {
    setShowValidationErrors(true)
  }, [])

  const onCloseModal = () => navigate('/roles-permissions')

  const onSubmit = ({ name, permissions, deletedPermissions }: TFormValues) => {
    // update role name
    const updatedRoleName = {
      ...role,
      name: name,
    }

    // create role structure to update in api
    const roleToSend = {
      uuid: updatedRoleName?.uuid as string,
      name: updatedRoleName?.name as string,
      superadmin: updatedRoleName?.superadmin as boolean,
      organizationId: updatedRoleName?.organizationId as string,
    }

    // update role only when user change name
    if (role?.name !== name) {
      updateRole(roleToSend)
        .unwrap()
        .then(() => {
          navigate('/roles-permissions')
          toast.success('Success!')
        })
    }

    // we have to transform action and subject to their substitutes in api
    const transformPermissionsValues = permissions?.map((permission) => ({
      ...permission,
      subject: convertDisplayAppNameToApi(permission.subject as TAppTypesDisplaySubsitutes),
      action: convertActionToApiValue(permission.action as TActions),
    }))

    // filter permissions which are already created, then create structure compatibile with api
    const permissionsToCreate = transformPermissionsValues
      .filter(({ uuid }) => isEmpty(uuid))
      .map(({ action, subject }) => ({
        action: action as TActionsApiSubstitutes,
        subject: subject as TAppTypesApiSubstitutes,
        userRoleId: role?.uuid as string,
      }))

    // filter permissions to reject already created permission
    const permissionsToUpdate = transformPermissionsValues.filter(({ uuid }) => !isEmpty(uuid))

    const initialValuesPermissionsNames = initialValues?.permissions?.map(
      (permission) => permission?.subject
    )

    // It could be case that user can check and uncheck some permission, which doesn't mean that we have to send DELETE request,
    // that's why we compere all deletedPermissions with permissions from initial state
    const permissionsToDelete = deletedPermissions?.filter(({ subject }) =>
      initialValuesPermissionsNames.includes(subject)
    )

    // create permission
    if (!isEmpty(permissionsToCreate)) {
      createPermission(permissionsToCreate)
        .unwrap()
        .then(() => {
          navigate('/roles-permissions')
          toast.success('Success!')
        })
    }

    // update permission
    if (!isEmpty(permissionsToUpdate)) {
      updatePermission(permissionsToUpdate)
        .unwrap()
        .then(() => {
          navigate('/roles-permissions')
          toast.success('Success!')
        })
    }

    // delete permission
    if (!isEmpty(permissionsToDelete)) {
      permissionsToDelete.map(({ uuid }) => {
        deletePermission(uuid)
          .unwrap()
          .then(() => {
            navigate('/roles-permissions')
          })
      })
    }
  }

  if (isFetching) {
    return <Loader />
  }

  return (
    <Modal title="Edit Role" onClose={onCloseModal} className="-narrow">
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue, values }) => (
            <Form className="item-settings">
              <section className="item-settings__section ">
                <div className="content-wrapper">
                  <Field
                    component={TextBox}
                    label="Role Name"
                    name="name"
                    showValidationErrors={showValidationErrors}
                    onChange={(value: string) => {
                      setFieldValue('name', value)
                    }}
                    value={values?.name}
                    error={errors?.name}
                  />
                </div>
              </section>
              <div className="item-settings__border-break"></div>
              <h3 className="header pt-mxs">Permissions</h3>
              <section>
                {PERMISSIONS_APPS?.map(
                  ({
                    name,
                    uuid,
                    subApps,
                    disabledCheckbox,
                    disabledDropdown,
                    actions,
                    isDropdown,
                    checked,
                  }) => (
                    <div className="content-wrapper -flags-wrapper -border-top-none" key={uuid}>
                      <Field
                        component={CheckAccordion}
                        label={name}
                        selectOptions={actions}
                        defaultSelectOption={setDefaultSelectOption(
                          values.permissions as TPermission[],
                          name as TAppTypesDisplaySubsitutes
                        )}
                        additionalAccordionOptions={subApps}
                        onChange={(value: TReturnDropdownValue) =>
                          setEditRoleValues(
                            value,
                            values.permissions,
                            values.deletedPermissions,
                            setFieldValue
                          )
                        }
                        disabledCheckbox={disabledCheckbox}
                        disabledDropdown={disabledDropdown}
                        isDropdown={isDropdown}
                        checked={setCheckedPermission(
                          values?.permissions as TPermission[],
                          name as TAppTypesDisplaySubsitutes,
                          checked
                        )}
                      />
                    </div>
                  )
                )}
              </section>
              <div className="btn-wrapper">
                <button
                  className="btn -update"
                  onClick={onClickSaveButton}
                  disabled={disableUpdateButton(dirty, errors)}
                  type="submit"
                >
                  Save
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default EditRole
